import axios, { type AxiosInstance } from 'axios';
import { pickBody } from '@/api/helpers';
import { attachAuthInterceptors } from '@/services/auth';

const CALENDAR_API = String(
  import.meta.env.VITE_CALENDAR_API_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'
).replace(/\/+$/, '');

const MEET_AUDIENCE = String(import.meta.env.VITE_MEET_AUTH_AUDIENCE || 'meet-api');

export type MeetingRoom = {
  roomId: string;
  name: string;
  createdAt: string;
  scheduledAt: string | null;
  duration: number | null;
  isScheduled: boolean;
  endedAt: string | null;
  status: 'scheduled' | 'ongoing' | 'completed' | string;
};

type ListRoomsResponse = {
  success?: boolean;
  rooms?: MeetingRoom[];
  pagination?: { total: number; page: number; limit: number; pages: number };
};

export type CreateMeetingInput = {
  name: string;
  scheduledAt: string;
  duration: number;
};

type CreateRoomResponse = {
  success?: boolean;
  room: {
    roomId: string;
    name: string;
    createdAt: string;
    createdBy: string;
    scheduledAt: string | null;
    duration: number;
    isScheduled: boolean;
    settings?: Record<string, unknown>;
  };
};

function createMeetClient(): AxiosInstance {
  const client = axios.create({
    baseURL: `${CALENDAR_API}/api/v1/integrations/meet`,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  });

  // Token is read from cache on request; refresh is called ONLY on 401 response.
  attachAuthInterceptors(client, { audience: MEET_AUDIENCE });

  return client;
}

const meetClient = createMeetClient();

type ListMeetingsParams = { from?: string; to?: string };

const listPage = (params: { page: number; limit: number; from?: string; to?: string }): Promise<ListRoomsResponse> =>
  meetClient.get<unknown>('/rooms', { params }).then((res) => {
    const raw = pickBody<ListRoomsResponse>(res.data);
    return raw ?? {};
  });

const listRange = async (params: ListMeetingsParams = {}): Promise<MeetingRoom[]> => {
  const limit = 100;
  let page = 1;
  let pages = 1;
  const all: MeetingRoom[] = [];

  try {
    while (page <= pages) {
      const payload = await listPage({ page, limit, from: params.from, to: params.to });
      if (Array.isArray(payload.rooms)) all.push(...payload.rooms);
      pages = payload.pagination?.pages ?? 1;
      page += 1;
    }
  } catch (error) {
    if (axios.isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
      return [];
    }
    throw error;
  }

  return all;
};

const create = (input: CreateMeetingInput): Promise<CreateRoomResponse['room']> =>
  meetClient.post<unknown>('/rooms', input).then((res) => {
    const raw = pickBody<CreateRoomResponse>(res.data);
    return raw.room;
  });

export const meetingsApi = { listRange, create };
